import React from 'react';
import { Link } from 'react-router-dom';
import { Notice } from '../../types/Notice';
import { getBaseUrl } from '../../services/api';

interface NoticeCardProps {
  notice: Notice;
  showFullContent?: boolean;
  onReadMore?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onArchive?: () => void;
  onPublish?: () => void;
  showActions?: boolean;
  className?: string;
}

const NoticeCard: React.FC<NoticeCardProps> = ({
  notice,
  showFullContent = false,
  onReadMore,
  onEdit,
  onDelete,
  onArchive,
  onPublish,
  showActions = false,
  className = ''
}) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#0d6efd';
      case 'low': return '#198754';
      default: return '#6c757d';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return 'ℹ️';
      case 'low': return '📢';
      default: return '📄';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'central': return '🏛️';
      case 'building': return '🏢';
      default: return '📄';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = notice.expires_at && new Date(notice.expires_at) < new Date();
  const isScheduled = notice.scheduled_at && new Date(notice.scheduled_at) > new Date();

  const transformContent = (html: string) => {
    const baseUrl = getBaseUrl();
    // Replace img src to ensure absolute non-localhost URLs
    return html.replace(/<img([^>]+)src=["']([^"']+)["']([^>]*)>/gi, (_match, before, src, after) => {
      let newSrc = src;
      try {
        if (src.startsWith('http://') || src.startsWith('https://')) {
          const u = new URL(src);
          if (u.hostname.includes('localhost')) {
            newSrc = `${baseUrl}${u.pathname}`;
          }
        } else if (!src.startsWith('data:')) {
          // Relative path → prefix base
          newSrc = `${baseUrl}${src.startsWith('/') ? src : '/' + src}`;
        }
      } catch (e) {
        // Best-effort: if it looks like a storage path, prefix base
        if (/^\/?storage\/.*/i.test(src)) {
          newSrc = `${baseUrl}${src.startsWith('/') ? src : '/' + src}`;
        }
      }
      return `<img${before}src="${newSrc}"${after}>`;
    });
  };

  return (
    <div className={`notice-card ${className}`}>
      {/* Header */}
      <div className="notice-header">
        <div className="notice-meta">
          <div className="notice-type">
            <span className="type-icon">{getTypeIcon(notice.type)}</span>
            <span className="type-text">
              {notice.type === 'central' ? 'Central Committee' : 'Building Committee'}
            </span>
            {notice.building && (
              <span className="building-name"> - {notice.building.name}</span>
            )}
          </div>
          <div className="notice-priority">
            <span 
              className="priority-badge"
              style={{ backgroundColor: getPriorityColor(notice.priority) }}
            >
              {getPriorityIcon(notice.priority)} {notice.priority.toUpperCase()}
            </span>
            {notice.is_urgent && (
              <span className="urgent-badge">URGENT</span>
            )}
          </div>
        </div>
        <div className="notice-date">
          {formatDate(notice.created_at)}
        </div>
      </div>

      {/* Status Indicators */}
      <div className="notice-status">
        {isExpired && (
          <span className="status-badge expired">EXPIRED</span>
        )}
        {isScheduled && (
          <span className="status-badge scheduled">SCHEDULED</span>
        )}
        {notice.status === 'draft' && (
          <span className="status-badge draft">DRAFT</span>
        )}
        {notice.status === 'archived' && (
          <span className="status-badge archived">ARCHIVED</span>
        )}
      </div>

      {/* Title (link to details when not full) */}
      {showFullContent ? (
        <h3 className="notice-title">{notice.title}</h3>
      ) : (
        <h3 className="notice-title">
          <Link to={`/notices/${notice.id}`} className="notice-title-link">
            {notice.title}
          </Link>
        </h3>
      )}

      {/* Content */}
      <div className="notice-content">
        {showFullContent ? (
          <div 
            className="notice-full-content"
            dangerouslySetInnerHTML={{ __html: transformContent(notice.content) }}
          />
        ) : (
          <div className="notice-excerpt">
            {notice.excerpt || notice.content.replace(/<[^>]*>/g, '').substring(0, 200) + '...'}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="notice-footer">
        <div className="notice-stats">
          <span className="views-count">
            👁️ {notice.views_count} views
          </span>
          {notice.creator && (
            <span className="creator">
              By {notice.creator.name}
            </span>
          )}
        </div>

        {/* Attachments */}
        {Array.isArray((notice as any).attachments) && (notice as any).attachments.length > 0 ? (
          <div className="attachments">
            <div className="attachment-title">Attachments</div>
            <ul className="attachment-list">
              {(notice as any).attachments.map((att: any) => (
                <li key={att.id} className="attachment-item">
                  📎 <a href={att.url} target="_blank" rel="noopener noreferrer">{att.file_name}</a>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          notice.attachment_path && (
            <div className="attachment">
              📎 <a href={notice.attachment_path} target="_blank" rel="noopener noreferrer">
                Download Attachment
              </a>
            </div>
          )
        )}

        <div className="notice-actions">

          {!showFullContent && (
            <Link 
              to={`/notices/${notice.id}`}
              className="btn btn-primary btn-sm"
              style={{ marginLeft: 8 }}
            >
              View
            </Link>
          )}
          
          {showActions && (
            <div className="admin-actions">
              {onEdit && (
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  onClick={onEdit}
                >
                  Edit
                </button>
              )}
              {onPublish && notice.status === 'draft' && (
                <button 
                  className="btn btn-outline-success btn-sm"
                  onClick={onPublish}
                >
                  Publish
                </button>
              )}
              {onArchive && notice.status === 'published' && (
                <button 
                  className="btn btn-outline-warning btn-sm"
                  onClick={onArchive}
                >
                  Archive
                </button>
              )}
              {onDelete && (
                <button 
                  className="btn btn-outline-danger btn-sm"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoticeCard;
